import type { CaseFileRecord } from "@/components/MedicalChronology/Files/types";
import type { MedChronTemplate } from "@/components/MedicalChronology/Visits/types";
import type { MedChronViewTab } from "@/components/MedicalChronology/types";
import type { ChronologyVersion, MedChronFirmContext } from "@/components/MedicalChronology/types";
import type { AuthContextValue } from "@/contexts/AuthContext";
import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import { createContext, useContext } from "react";
import type { NavigateFunction } from "react-router-dom";

export interface MedicalChronologyContextValue {
  caseId: string;
  caseName: string | null;
  updateCaseName: (newCaseName: string) => void;
  physicianName: string | null;
  caseStatusId: number | null;
  template: MedChronTemplate | null;
  templateId: string | null;
  fileMapping: Record<string, string>;
  caseFiles: CaseFileRecord[];
  caseFilesLoading: boolean;
  caseFilesError: string | null;

  versions: ChronologyVersion[];
  selectedVersionId: string | null;
  currentVersion: ChronologyVersion | null;
  isVersionActionLoading: boolean;
  setSelectedVersionId: (versionId: string) => void;
  openVersionNameModal: (versionName: string) => void;
  setVersionAsCurrent: () => Promise<void>;
  duplicateVersion: (versionId: string) => Promise<void>;
  deleteVersion: (versionId: string) => Promise<void>;
  versionNameModal: {
    open: boolean;
    value: string;
    initialValue: string;
  };
  setVersionNameModalValue: (value: string) => void;
  saveVersionName: () => Promise<void>;
  closeVersionNameModal: () => void;

  citationPanel: {
    isOpen: boolean;
    fileId: string | null;
    fileName: string;
    fileUrl: string;
    pageNumber: number;
    width: number;
    onViewerLoadError: (error?: unknown) => void;
    onViewerLoadSuccess: () => void;
  };
  openCitation: (fileId: string, pageNumbers: number[], visitId?: string) => Promise<void>;
  openVisitCitation: (citation: MedVisitCitation) => Promise<void>;
  closeCitationPanel: () => void;
  setCitationPage: (page: number) => void;
  setCitationPanelWidth: (width: number) => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;

  activeView: MedChronViewTab;
  setActiveView: (tab: MedChronViewTab) => void;

  isPageLoading: boolean;
  markCaseReviewed: () => Promise<void>;
  navigate: NavigateFunction;
  hasPermission: AuthContextValue["hasPermission"];
  firmContext: MedChronFirmContext | undefined;
}

const MedicalChronologyContext = createContext<MedicalChronologyContextValue | undefined>(
  undefined
);

export const useMedicalChronology = (): MedicalChronologyContextValue => {
  const value = useContext(MedicalChronologyContext);
  if (!value) {
    throw new Error("useMedicalChronology must be used within MedicalChronologyProvider");
  }
  return value;
};

export { MedicalChronologyContext };
